import _ from 'underscore';
import Backbone from 'backbone';
import DemoCompareInsight from 'models/insight/DemoCompareInsight';
import DemoComparePreference from 'models/insight/DemoComparePreference';
import ViewerSocialProfile from 'models/user/ViewerSocialProfile';
import dotMarkerTemplate from 'templates/insight/dotMarker.html';

// mapping from insight category name to preference name (as it should be sent to the API) and default position (will be displayed in ascending order)
var DemoComparePreferenceMapper = {
    STEPS: {
        name: 'steps',
        index: 1
    },
    FLOORS: {
        name: 'stair_climbing',
        index: 2
    },
    SLEEP: {
        name: 'sleep',
        index: 3
    },
    RUNNING: {
        name: 'running',
        index: 4
    },
    CYCLING: {
        name: 'cycling',
        index: 5
    },
    LAP_SWIMMING: {
        name: 'lap_swimming',
        index: 6
    },
    OPEN_WATER_SWIMMING: {
        name: 'open_water_swimming',
        index: 7
    }
};

var insightsUtil = {
    STATUS: {
        UNREAD: 'UNREAD',
        READ: 'READ',
        DISMISSED: 'DISMISSED',
        DELETED: 'DELETED',
        FAVORITED: 'FAVORITED',
        DISLIKED: 'DISLIKED'
    },

    CHART_VER: 4.0,

    // Splits the Insight JSON into objects and matches the view as provided in insightComponentViews
    getInsightComponents: function(model, insightComponentViews, callback, context) {
        if (!model || !insightComponentViews || !callback || !context) {
            return false;
        }

        _.each(model.attributes, function(object) {
            if (typeof object === 'object') {
                _.each(insightComponentViews, function(fn, component) {
                    if (_.has(object, component)) {
                        callback.apply(context, [component, object[component]]);
                    }
                });
            }
        });
    },

    hasActionComponent: function(insight) {
        if (!insight) {
            return false;
        }

        var hasActionComponent = false;

        _.each(insight.attributes, function(object) {
            if (typeof object === 'object' && object.hasOwnProperty('action')) {
                hasActionComponent = true;
            }
        });

        return hasActionComponent;
    },

    getChartType: function(charts, insightChartViews, callback, context) {
        if (!charts || !insightChartViews || !callback || !context) {
            return false;
        }

        _.each(charts, function(chart, index) {
            if (!chart) {
                return false;
            }

            if (_.has(insightChartViews, chart.chartType)) {
                callback.apply(context, [chart.chartType, chart, index]);
            }
        });
    },

    generateChartMarker: function(color, title, text, marginRight, circleDiameter) {
        // color - dot background color -- e.g.: 'red', '#3EB9F0', 'rgba(0,0,0,0.05)'
        // title - title attribute for dot, visible when hovering over dot -- e.g.: any string
        // text - additional text on a new line underneath the dot (used values: 12a, 12p, 6)
        // marginRight - distance between dot and text -- e.g.: '10px', '6px', '1.2em'
        // circleDiameter - diameter of circle -- e.g.: '10px', '1em'

        var textPosition = '';
        var dotTemplate = _.template(dotMarkerTemplate);

        color = color || '#11A9ED';
        title = title || title === 0 ? title : '';
        marginRight = marginRight || 0;
        circleDiameter = circleDiameter || '8px';

        if (text === 0 || text) {
            textPosition = -(String(text).length * 3) + 'px'; // negative offset of 3px for each extra character to center the text under the dot
        }

        return dotTemplate({
            text: text,
            textPosition: textPosition,
            color: color,
            marginRight: marginRight,
            title: title,
            circleDiameter: circleDiameter
        });
    },
    getAxisIntervalSteps: function(chartData) {
        var defaultInterval = 1;
        if (chartData.length > 20 && chartData.length < 30) {
            return 2;
        } else if (chartData.length >= 30 && chartData.length <= 50) {
            return 4;
        } else if (chartData.length > 50) {
            return 10;
        }

        return defaultInterval;
    },

    getCategoryIcons: function() {
        return {
            SLEEP: 'icon-sleep',
            STEPS: 'icon-steps-1',
            RUNNING: 'icon-activity-running',
            CYCLING: 'icon-activity-cycling',
            FLOORS: 'icon-floors-climbed',
            LAP_SWIMMING: 'icon-activity-swimming',
            OPEN_WATER_SWIMMING: 'icon-activity-swimming',
            uncategorized: 'icon-insights'
        };
    },

    // DEMO COMPARE PREFERENCE FUNCTIONS

    /**
     * Performs initial save of all selections as preferences, setting the appropriate index and visibility to null.
     * This will only happen on first page load, when the list of preferences for this user is empty.
     * After this, the preference list will contain all activity types
     */
    saveIndexedPreferences: function(demoSelections, demoPreferences) {
        _.each(
            demoSelections.get('activityMapping'),
            function(selection) {
                demoPreferences.add(
                    new DemoComparePreference({
                        type: DemoComparePreferenceMapper[selection.activity].name,
                        show: null,
                        index: DemoComparePreferenceMapper[selection.activity].index
                    })
                );
            },
            this
        );

        Backbone.sync('update', demoPreferences);
    },

    /**
     * Adds missing preferences from selections, setting the appropriate index and visibility to null.
     * This happens when the list of preferences for this user has some missing items.
     * After this, the preference list will contain all activity types
     */
    fixIndexedPreferences: function(demoSelections, demoPreferences) {
        let lastIndex = 0;
        let syncPreference = false;
        // get the last index
        demoPreferences.each((preference) => {
            let currentIndex = preference.get('index');
            if (currentIndex > lastIndex) lastIndex = currentIndex;
        });

        // add missing preferences from selections
        demoSelections.get('activityMapping').forEach((selection) => {
            let preference = demoPreferences.findWhere({
                type: DemoComparePreferenceMapper[selection.activity].name
            });

            if (!preference) {
                demoPreferences.add(
                    new DemoComparePreference({
                        type: DemoComparePreferenceMapper[selection.activity].name,
                        show: null,
                        index: ++lastIndex
                    })
                );
                syncPreference = true;
            }
        });

        if (syncPreference) Backbone.sync('update', demoPreferences);
    },

    updatePreferenceIndexes: function(newIndexes, demoPreferences, callback) {
        _.map(newIndexes, function(index, key) {
            var preference = demoPreferences.findWhere({
                type: DemoComparePreferenceMapper[key].name
            });

            preference.set({
                index: index
            });
        });

        Backbone.sync('update', demoPreferences, {
            success: function() {
                if (callback) {
                    callback();
                }
            }
        });
    },

    /**
     * Maps through the list of API provided selections, checks whether the usage indicators allow for the specific selection
     * and converts the selections to the objects used further for charts and filters display
     */
    mapIndicatorsToSelections: function(demoSelections, demoPreferences, usageIndicators) {
        let customizedActivityTypes = [];
        demoSelections.get('activityMapping').forEach((selection) => {
            let preference = this.getActivityFromPrefs(selection.activity, demoPreferences);
            if (preference) {
                let indicator;
                switch (selection.activity) {
                    case DemoCompareInsight.ACTIVITY_TYPES.STEPS:
                    case DemoCompareInsight.ACTIVITY_TYPES.SLEEP:
                        indicator = ViewerSocialProfile.isWellnessUser();
                        break;
                    case DemoCompareInsight.ACTIVITY_TYPES.FLOORS:
                        indicator = usageIndicators.deviceBasedIndicators.hasFloorCountCapableDevice;
                        break;
                    case DemoCompareInsight.ACTIVITY_TYPES.RUNNING:
                        try {
                            indicator =
                                usageIndicators.performanceBasedIndicators.activityIndicators.running.lastActivityDate;
                        } catch (e) {
                            indicator = null;
                        }
                        break;
                    case DemoCompareInsight.ACTIVITY_TYPES.CYCLING:
                        try {
                            indicator =
                                usageIndicators.performanceBasedIndicators.activityIndicators.cycling.lastActivityDate;
                        } catch (e) {
                            indicator = null;
                        }
                        break;
                    case DemoCompareInsight.ACTIVITY_TYPES.LAP_SWIMMING:
                        try {
                            indicator =
                                usageIndicators.performanceBasedIndicators.activityIndicators.lap_swimming
                                    .lastActivityDate;
                        } catch (e) {
                            indicator = null;
                        }
                        break;
                    case DemoCompareInsight.ACTIVITY_TYPES.OPEN_WATER_SWIMMING:
                        try {
                            indicator =
                                usageIndicators.performanceBasedIndicators.activityIndicators.open_water_swimming
                                    .lastActivityDate;
                        } catch (e) {
                            indicator = null;
                        }
                        break;
                    default:
                        indicator = true;
                }

                let enabled;
                if (!_.isNull(preference.get('show'))) {
                    enabled = preference.get('show');
                } else {
                    enabled = indicator ? true : false;
                }

                customizedActivityTypes.push({
                    activityType: selection.activity,
                    activityName: selection.localizedActivity,
                    fields: selection.fields,
                    enabled: enabled,
                    index: preference.get('index')
                });
            }
        }, this);

        return customizedActivityTypes;
    },

    getActivityFromPrefs: function(activity, prefs) {
        return _.find(prefs.models, function(model) {
            return model.get('type') === DemoComparePreferenceMapper[activity].name;
        });
    },

    // DEMO COMPARE CHART OPTIONS

    getDemoCompareChartColors: function() {
        return {
            grey: '#8C8C8C',
            columns: ['#11a9ed'],

            emphasize: {
                NEUTRAL: '#ff9d0d' // orange
            }
        };
    },

    getDemoCompareChartTitles: function(titles) {
        var titleStyles = {
            fontSize: '12px',
            color: '#555'
        };

        return {
            xAxis: {
                title: {}
            },

            yAxis: {
                title: {
                    text: titles.y,
                    style: titleStyles,
                    margin: -5,
                    x: -15
                }
            }
        };
    }
};

export default insightsUtil;
